import { z } from "zod";
import { isoDateTimeSchema, uuidSchema } from "./common.js";

// Field jobs (FLD-01 to FLD-04; D-59 to D-63). Mirrors FieldJob in
// contracts/openapi.yaml.

export const fieldJobTypeSchema = z.enum([
  "read",
  "locate",
  "verify_serial",
  "meter_test",
  "exchange",
  "survey_premises",
]);
export type FieldJobType = z.infer<typeof fieldJobTypeSchema>;

export const fieldJobStatusSchema = z.enum([
  "raised",
  "dispatched",
  "completed",
  "aborted",
  "cancelled",
]);
export type FieldJobStatus = z.infer<typeof fieldJobStatusSchema>;

export const abortReasonSchema = z.enum(["no_access", "unsafe", "not_found", "other"]);
export type AbortReason = z.infer<typeof abortReasonSchema>;

export const fieldJobResultSchema = z
  .object({
    readValue: z.number(),
    readAt: isoDateTimeSchema,
    serialObserved: z.string(),
    locationType: z.string(),
    gridRef: z.string(),
    locationDescription: z.string(),
    accessNotes: z.string(),
    meterWorking: z.boolean(),
    exchanged: z
      .object({
        finalReadOld: z.number(),
        newSerial: z.string(),
        newSizeMm: z.number().int(),
        newDials: z.number().int(),
        initialReadNew: z.number(),
      })
      .partial(),
    supplyConfirmed: z.boolean(),
  })
  .partial();
export type FieldJobResult = z.infer<typeof fieldJobResultSchema>;

export const fieldJobTargetSchema = z.object({
  uprn: z.string().nullable(),
  address: z.string(),
});

export const fieldJobSchema = z.object({
  id: uuidSchema,
  ref: z.string(),
  externalRef: z.string().nullable(),
  type: fieldJobTypeSchema,
  supplyPointId: uuidSchema.nullable(),
  spid: z.string().nullable(),
  meterId: uuidSchema.nullable(),
  meterSerial: z.string().nullable(),
  findingId: uuidSchema.nullable(),
  findingRef: z.string().nullable(),
  assigneePartyId: uuidSchema.nullable(),
  assigneePartyName: z.string().nullable(),
  status: fieldJobStatusSchema,
  brief: z.string().nullable(),
  target: fieldJobTargetSchema.nullable(),
  raisedAt: isoDateTimeSchema,
  dueAt: isoDateTimeSchema.nullable(),
  dispatchedAt: isoDateTimeSchema.nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  abortReason: abortReasonSchema.nullable(),
  result: fieldJobResultSchema.nullable(),
  /** For an exchange job: whether its exchange reached the registry as MeterExchanged (D-61). */
  exchangeRecorded: z.boolean(),
  isSynthetic: z.boolean(),
});
export type FieldJob = z.infer<typeof fieldJobSchema>;

export const fieldJobPageSchema = z.object({
  items: z.array(fieldJobSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int(),
});
export type FieldJobPage = z.infer<typeof fieldJobPageSchema>;

export const completeFieldJobSchema = z
  .object({
    outcome: z.enum(["completed", "aborted"]),
    abortReason: abortReasonSchema.optional(),
    result: fieldJobResultSchema.optional(),
    evidenceIds: z.array(z.string().uuid()).max(50).default([]),
  })
  .refine((b) => b.outcome !== "aborted" || b.abortReason !== undefined, {
    message: "an aborted job needs its reason",
    path: ["abortReason"],
  });
export type CompleteFieldJob = z.infer<typeof completeFieldJobSchema>;

export const createFieldJobSchema = z
  .object({
    type: fieldJobTypeSchema.optional(),
    supplyPointId: uuidSchema.optional(),
    meterId: uuidSchema.optional(),
    findingId: uuidSchema.optional(),
    assigneePartyId: uuidSchema.optional(),
  })
  .refine(
    (b) => b.findingId !== undefined || (b.type !== undefined && b.supplyPointId !== undefined),
    {
      message: "name a findingId, or a type and a supplyPointId",
    },
  );
export type CreateFieldJob = z.infer<typeof createFieldJobSchema>;
